import React, { useState, useEffect } from "react";
import {
  Text,
  Shimmer,
  ShimmerElementType,
  IconButton,
} from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { listIntegrations, listIntegrationConfigurations } from "@/api/integrations";
import styles from "@/styles/HomePage.module.css";

interface MetricCardProps {
  title: string;
  value: number;
  color?: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  color = "#0078d4",
  onClick,
}) => (
  <div
    className={styles.metricCard}
    onClick={onClick}
  >
    <Text variant="large" className={styles.metricCardTitle}>
      {title}
    </Text>
    <div className={styles.metricCardValue} style={{ backgroundColor: color }}>
      {value}
    </div>
  </div>
);

const ShimmerCard: React.FC = () => (
  <div
    style={{
      backgroundColor: "#ffffff",
      border: "1px solid #e1e5e9",
      borderRadius: "8px",
      padding: "24px",
      minHeight: "120px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    }}
  >
    <div style={{ marginBottom: "12px", width: "100%" }}>
      <Shimmer
        shimmerElements={[
          { type: ShimmerElementType.line, width: "80%", height: 20 },
        ]}
        width="100%"
      />
    </div>
    <div style={{ width: "60%" }}>
      <Shimmer
        shimmerElements={[
          { type: ShimmerElementType.line, width: "100%", height: 32 },
        ]}
        width="100%"
      />
    </div>
  </div>
);

const IntegrationDashboardPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIntegrations, setActiveIntegrations] = useState(0);
  const [activeGlobalConfigurations, setActiveGlobalConfigurations] = useState(0);
  const [activeConfigurations, setActiveConfigurations] = useState(0);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [intData, cfgData] = await Promise.all([
        listIntegrations(undefined, 1000, true),
        listIntegrationConfigurations(undefined, undefined, undefined, undefined, 1000),
      ]);

      const allConfigs = cfgData?.result || [];
      setActiveIntegrations(intData?.result?.length || 0);
      setActiveGlobalConfigurations(
        allConfigs.filter((c) => c.isActive && c.scopeType === 1).length
      );
      setActiveConfigurations(
        allConfigs.filter((c) => c.isActive && c.scopeType >= 2).length
      );
    } catch {
      setError("Failed to load integration dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2 className="page-title">Overview</h2>
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh Dashboard"
          ariaLabel="Refresh Dashboard"
          onClick={() => fetchData()}
          disabled={isLoading}
          styles={{
            root: { width: 40, height: 40 },
            rootHovered: { backgroundColor: "#f3f2f1" },
            icon: {
              fontSize: 18,
              color: isLoading ? "#a19f9d" : "#0078d4",
            },
            rootPressed: { backgroundColor: "#e1dfdd" },
            rootDisabled: { backgroundColor: "transparent" },
          }}
        />
      </div>

      <div className={styles.metricsGrid}>
        {isLoading ? (
          <>
            <ShimmerCard />
            <ShimmerCard />
            <ShimmerCard />
          </>
        ) : error ? (
          <div className={styles.errorState}>
            <Text variant="medium">{error}</Text>
          </div>
        ) : (
          <>
            <MetricCard
              title="Active Integrations"
              value={activeIntegrations}
              color="#0078d4"
              onClick={() => navigate("/integrations/catalog")}
            />
            <MetricCard
              title="Active Global Configurations"
              value={activeGlobalConfigurations}
              color="#106ebe"
              onClick={() => navigate("/integrations/configurations")}
            />
            <MetricCard
              title="Active Customer Configurations"
              value={activeConfigurations}
              color="#107c10"
              onClick={() => navigate("/integrations/configurations")}
            />
          </>
        )}
      </div>

      <Text variant="large" className={styles.footerText}>
        Use the navigation menu to manage integration catalog, configurations,
        pricing, and usage tracking.
      </Text>
    </div>
  );
};

export default IntegrationDashboardPage;
